// store/slices/fileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  uploadFile,
  processUploadedFile,
  applyModels,
  generativeEngineInsightsResult,
} from "../../services/fileService";

export const uploadFileThunk = createAsyncThunk(
  "file/upload",
  async (payload = {}, { rejectWithValue, signal }) => {
    const { file, processName, subProcess, fileName } = payload;

    if (!file) {
      return rejectWithValue("No file provided for upload");
    }

    try {
      const response = await uploadFile({
        file,
        processName,
        subProcess,
        fileName,
      }, { signal });

      const raw = response;

      const filePath =
        raw?.file_path || raw?.filePath || raw?.data?.file_path || null;

      const lastResponse = raw?.data ?? raw;

      return {
        raw,
        filePath,
        lastResponse,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Upload failed"
      );
    }
  }
);

// second step thunk
export const processFileThunk = createAsyncThunk(
  "file/process",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await processUploadedFile(payload);
      const processResponse = response;
      return processResponse;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Process failed"
      );
    }
  }
);


export const generativeEngineInsightsResultThunk = createAsyncThunk(
  "file/generativeEngineInsightsResult",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await generativeEngineInsightsResult(payload);
      return data ?? null;
    } catch (error) {
      const serverPayload = error?.response?.data ?? null;
      const message =
        serverPayload?.message || error?.message || "Insights generation failed";
      return rejectWithValue({ message, serverPayload });
    }
  }
);


// third step thunk to apply models
export const applyModelsThunk = createAsyncThunk(
  "file/applyModels",
  async (payload, { rejectWithValue, signal }) => {
    try {
      const data = await applyModels(payload, { signal });
      return data ?? {};
    } catch (error) {
      console.log("error : ", error)
      return rejectWithValue(
        error?.response?.data?.detail || error?.response?.data?.message || error.message || "Apply models failed"
      );
    }
  }
);

const initialState = {
    files: [],
    loading: false,
    error: null,
    lastResponse: null,
    uploadedFilePath: null,
    localFileMeta: null,
    processing: false,
    processError: null,
    processResponse: null,
    applying: false,
    applyError: null,
    applyResponse: null,
    generativeEngineInsightsResult: null,
    insightsGenerating: false,
    insightsGenerateError: null,

    activeStep: 0, // 0 = not started, 1..N = step index
    totalSteps: 4, // default number of steps for processing
};

const fileSlice = createSlice({
  name: "file",
  initialState,
  reducers: {
    clearUploadState(state) {
      state.uploadedFilePath = null;
      state.localFileMeta = null;
      state.lastResponse = null;
      state.error = null;
      state.applyResponse = null;
      state.generativeEngineInsightsResult = null;
      state.processError = null;
      state.processing = false;
      state.activeStep = 0;
      state.insightsGenerating = false;
      state.insightsGenerateError = null;
    },
    resetAll() {
      return initialState;
    },

    setLocalFileMeta(state, action) {
      state.localFileMeta = action.payload;
    },

    clearProcessError(state) {
      state.processError = null;
    },

    // NEW actions to control steps
    setActiveStep(state, action) {
      state.activeStep = action.payload;
    },
    setTotalSteps(state, action) {
      state.totalSteps = action.payload;
    },
    resetSteps(state) {
      state.activeStep = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // upload
      .addCase(uploadFileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastResponse = null;
        state.uploadedFilePath = null;
        state.processError = null;
        state.activeStep = 0;
      })
      .addCase(uploadFileThunk.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};

        // normalized fields
        state.lastResponse = payload.lastResponse ?? null;
        state.uploadedFilePath = payload.filePath ?? null;

        // --- STORE ONLY SERIALIZABLE DATA HERE ---
        state.files.push({
          filePath: payload.filePath ?? null,
          uploadedAt: new Date().toISOString(),
        });

        state.activeStep = 1;
      })
      .addCase(uploadFileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Upload failed";
        state.uploadedFilePath = null;
        state.activeStep = 0;
      })

      // generativeEngineInsightsResult
      .addCase(generativeEngineInsightsResultThunk.pending, (state) => {
        state.insightsGenerating = true;
        state.insightsGenerateError = null;
        state.generativeEngineInsightsResult = null;
      })
      .addCase(generativeEngineInsightsResultThunk.fulfilled, (state, action) => {
        state.insightsGenerating = false;
        state.generativeEngineInsightsResult = action.payload ?? null;
      })
      .addCase(generativeEngineInsightsResultThunk.rejected, (state, action) => {
        state.insightsGenerating = false;
        state.insightsGenerateError = action.payload?.message || "Apply models failed";
      })

      // process
      .addCase(processFileThunk.pending, (state) => {
        state.processing = true;
        state.processError = null;
        state.processResponse = null;
        if (state.activeStep < 2) state.activeStep = 2;
      })
      .addCase(processFileThunk.fulfilled, (state, action) => {
        state.processing = false;
        state.processResponse = action.payload || null;
        state.activeStep = state.totalSteps;
      })
      .addCase(processFileThunk.rejected, (state, action) => {
        state.processing = false;
        state.processError = action.payload || "Process failed";
      })

      // APPLY MODELS
      .addCase(applyModelsThunk.pending, (state) => {
        state.applying = true;
        state.applyError = null;
        state.applyResponse = null;
      })
      .addCase(applyModelsThunk.fulfilled, (state, action) => {
        state.applying = false;
        // action.payload expected to be the server response object:
        // { file_name, applied_models, blob_download_link }
        state.applyResponse = action.payload ?? null;
      })
      .addCase(applyModelsThunk.rejected, (state, action) => {
        state.applying = false;
        state.applyError = action.payload || "Apply models failed";
      });
  },
});

export const {
  clearUploadState,
  resetAll,
  setActiveStep,
  setTotalSteps,
  resetSteps,
  setLocalFileMeta,
  clearProcessError,
} = fileSlice.actions;
export default fileSlice.reducer;
